/** @format */

// src/components/RelayList.js
import { useState } from '@wordpress/element';
import Swal from 'sweetalert2';
import RelayForm from './RelayForm.js';

const RelayList = ({ relays = [], ageStandard = 'standard_hs', onRefresh }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingRelay, setEditingRelay] = useState(null);

	const getGlobalData = () => {
		if (typeof window !== 'undefined') {
			if (window.ammData) return window.ammData;
			if (window.wp?.ajax?.settings?.ammData) return window.wp.ajax.settings.ammData;
		}
		return null;
	};

	const handleOpenModal = (relay = null) => {
		setEditingRelay(relay);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setEditingRelay(null);
		setIsModalOpen(false);
	};

	const handleSave = () => {
		closeModal();
		if (onRefresh) onRefresh();
	};

	const handleDelete = (id) => {
		const globalData = getGlobalData();
		if (!globalData?.api_url) return;

		Swal.fire({
			title: 'Are you sure?',
			text: 'This will remove this structural relay team profile configuration.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Yes, Delete Team',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.isConfirmed) {
				fetch(`${globalData.api_url}/relays/${id}`, {
					method: 'DELETE',
					headers: { 'X-WP-Nonce': globalData.nonce || '' },
				})
					.then((res) => {
						if (!res.ok) throw new Error('Server rejected deletion request.');
						return res.json();
					})
					.then(() => {
						if (onRefresh) onRefresh();
						Swal.fire({
							title: 'Deleted!',
							text: 'The relay team has been safely removed.',
							icon: 'success',
							timer: 1500,
							showConfirmButton: false,
						});
					})
					.catch((err) => {
						console.error('Deletion tracking interface failure:', err);
						Swal.fire('Error', 'The server failed to delete the relay squad.', 'error');
					});
			}
		});
	};

	return (
		<div className="advanced-mm-relay-entries-wrapper bg-white rounded shadow-sm border p-3">
			<div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 border-bottom pb-2">
				<h5 className="fw-bold text-dark mb-0">Relay Teams</h5>
				<button className="btn btn-primary btn-sm d-flex align-items-center gap-2 shadow-sm" onClick={() => handleOpenModal(null)}>
					<i className="fas fa-plus-circle"></i>
					Add Relay Team
				</button>
			</div>

			{relays.length === 0 ? (
				<div className="text-center py-5 border border-dashed rounded-3 bg-light-subtle">
					<i className="fas fa-users-cog text-muted fa-2x mb-2"></i>
					<p className="text-dark fw-medium mb-1">No Relay Configurations Found</p>
					<p className="text-muted small mb-0">Predefine a relay crew profile above to open workspace availability.</p>
				</div>
			) : (
				<div className="table-responsive">
					<table className="table table-hover align-middle mb-0 small">
						<thead className="table-light text-uppercase text-secondary">
							<tr>
								<th>Relay Name</th>
								<th>Sex</th>
								<th>Affiliation</th>
								<th className="text-end pe-3">Actions</th>
							</tr>
						</thead>
						<tbody>
							{relays.map((relay) => {
								const targetSex = relay.sexCode || relay.gender || 'M';
								const badgeClass = targetSex === 'M' || targetSex === 'Male' ? 'text-bg-primary bg-opacity-10 text-primary border-primary-subtle' : targetSex === 'W' || targetSex === 'F' || targetSex === 'Female' ? 'text-bg-danger bg-opacity-10 text-danger border-danger-subtle' : 'text-bg-purple bg-opacity-10 text-purple border-purple-subtle';

								return (
									<tr key={relay.id}>
										<td>
											<div className="d-flex align-items-center gap-2">
												<i className="fas fa-users text-muted small"></i>
												<span className="fw-bold text-dark text-uppercase">{relay.relayName || 'Unnamed Squad'}</span>
											</div>
										</td>
										<td>
											<span className={`badge lh-sm border px-2 py-1 ${badgeClass}`}>{targetSex === 'M' ? 'Male (M)' : targetSex === 'F' ? 'Female (F)' : 'Mixed (X)'}</span>
										</td>
										<td className="text-muted">{relay.clubName || 'Independent'}</td>
										<td className="text-end pe-2">
											<div className="d-flex justify-content-end gap-1">
												<button className="btn btn-sm btn-light border px-2 shadow-none" onClick={() => handleOpenModal(relay)}>
													<i className="fas fa-edit text-secondary"></i>
												</button>
												<button className="btn btn-sm btn-light border px-2 shadow-none" onClick={() => handleDelete(relay.id)}>
													<i className="fas fa-trash-alt text-danger"></i>
												</button>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{isModalOpen && (
				<div className="amm-modal-overlay" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
					<div className="card shadow-lg amm-relay-modal-card m-auto" style={{ maxWidth: '520px', width: '100%' }}>
						<div className="card-header bg-white d-flex justify-content-between align-items-center py-3 border-bottom">
							<h5 className="fw-bold mb-0 text-dark">{editingRelay ? 'Modify Relay Team' : 'Create Relay Team'}</h5>
							<button type="button" className="btn-close shadow-none" onClick={closeModal}></button>
						</div>
						<div className="card-body bg-light-subtle p-4">
							<RelayForm onSave={handleSave} onCancel={closeModal} initialData={editingRelay} />
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default RelayList;
